/**
 * Logistics Driver Management (L5)
 * Version: 1.1.0
 * Description:
 *   Manage driver profiles and secure PIN-based authentication.
 *   Features Edit/Delete with role-based access (Admin/Manager only).
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  DialogContentText,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
  Chip,
  Avatar,
  CircularProgress,
  Paper,
  InputAdornment,
  Divider,
  Tooltip,
  FormControlLabel,
  Switch
} from '@mui/material';
import {
  Add as AddIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  LocalShipping as TruckIcon,
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { logisticsAPI } from '../../api';
import useAuthStore from '../../store/authStore';

interface Driver {
  id: string;
  full_name: string;
  phone: string;
  vehicle_number: string;
  is_active: boolean;
  created_at: string;
}

export default function LogisticsDrivers() {
  const { enqueueSnackbar } = useSnackbar();
  const user = useAuthStore((state) => state.user);
  const isAuthorized = user?.role === 'Admin' || user?.role === 'Manager';
  
  // State
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState<Driver | null>(null);
  const [showPin, setShowPin] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Form State
  const [formData, setFormData] = useState({
    full_name: '',
    phone: '',
    vehicle_number: '',
    pin: '',
    is_active: true
  });

  // Fetch Drivers
  const fetchDrivers = async () => {
    setLoading(true);
    try {
      const data = await logisticsAPI.getDrivers();
      setDrivers(data || []);
    } catch (error) {
      enqueueSnackbar('Failed to load drivers', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDrivers();
  }, [enqueueSnackbar]);

  // Handlers
  const handleOpenForm = (driver: Driver | null = null) => {
    if (driver) {
      setSelectedDriver(driver);
      setFormData({
        full_name: driver.full_name,
        phone: driver.phone,
        vehicle_number: driver.vehicle_number || '',
        pin: '', // PIN is never sent from backend
        is_active: driver.is_active
      });
    } else {
      setSelectedDriver(null);
      setFormData({ full_name: '', phone: '', vehicle_number: '', pin: '', is_active: true });
    }
    setOpen(true);
  };

  const handleCreateOrUpdate = async () => {
    if (!formData.full_name || !formData.phone) {
      enqueueSnackbar('Name and Phone are required', { variant: 'warning' });
      return;
    }
    
    // Validate PIN only if adding NEW driver or if provided during EDIT
    if ((!selectedDriver || formData.pin) && formData.pin.length !== 4) {
      enqueueSnackbar('PIN must be exactly 4 digits', { variant: 'warning' });
      return;
    }

    try {
      setSubmitting(true);
      if (selectedDriver) {
        await logisticsAPI.updateDriver(selectedDriver.id, formData);
        enqueueSnackbar('Driver profile updated!', { variant: 'success' });
      } else {
        await logisticsAPI.createDriver(formData);
        enqueueSnackbar('Driver registered successfully!', { variant: 'success' });
      }
      setOpen(false);
      fetchDrivers();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.detail || 'Failed to save driver', { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = (driver: Driver) => {
    setSelectedDriver(driver);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!selectedDriver) return;
    try {
      setSubmitting(true);
      await logisticsAPI.deleteDriver(selectedDriver.id);
      enqueueSnackbar('Driver profile deleted', { variant: 'success' });
      setDeleteDialogOpen(false);
      fetchDrivers();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.detail || 'Failed to delete driver', { variant: 'error' });
    } finally {
      setSubmitting(false);
      setSelectedDriver(null);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={4}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
            Driver Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage your delivery fleet and application access.
          </Typography>
        </Box>
        
        <Button 
          variant="contained" 
          startIcon={<AddIcon />} 
          disabled={!isAuthorized}
          onClick={() => handleOpenForm()}
        >
          Register New Driver
        </Button>
      </Stack>

      {loading && drivers.length === 0 ? (
        <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
      ) : (
        <Grid container spacing={3}>
          {drivers.map((driver) => (
            <Grid item xs={12} sm={6} md={4} key={driver.id}>
              <Card sx={{ p: 3, borderRadius: 2, position: 'relative', height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar sx={{ bgcolor: driver.is_active ? 'secondary.main' : 'text.disabled', width: 56, height: 56 }}>
                    {driver.full_name.charAt(0)}
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>{driver.full_name}</Typography>
                    <Chip 
                      label={driver.is_active ? 'Active' : 'Inactive'} 
                      size="small" 
                      color={driver.is_active ? 'success' : 'default'} 
                      sx={{ height: 20 }}
                    />
                  </Box>
                </Stack>
                
                <Stack spacing={1.5} mt={3} flexGrow={1}>
                  <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PhoneIcon fontSize="inherit" /> {driver.phone}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TruckIcon fontSize="inherit" /> {driver.vehicle_number || 'No Vehicle Assigned'}
                  </Typography>
                </Stack>
                
                <Divider sx={{ my: 2 }} />
                
                <Stack direction="row" justifyContent="flex-end" spacing={1}>
                  <Tooltip title={isAuthorized ? "Edit" : "Permission Denied"}>
                    <span>
                      <IconButton 
                        size="small" 
                        color="primary" 
                        disabled={!isAuthorized}
                        onClick={() => handleOpenForm(driver)}
                      >
                        <EditIcon fontSize="inherit" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={isAuthorized ? "Delete" : "Permission Denied"}>
                    <span>
                      <IconButton 
                        size="small" 
                        color="error" 
                        disabled={!isAuthorized}
                        onClick={() => handleDeleteClick(driver)}
                      >
                        <DeleteIcon fontSize="inherit" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
              </Card>
            </Grid>
          ))}
          {drivers.length === 0 && (
            <Grid item xs={12}>
              <Paper sx={{ py: 8, textAlign: 'center', color: 'text.disabled' }}>
                <PersonIcon sx={{ fontSize: 48, mb: 1 }} />
                <Typography variant="h6">No drivers found.</Typography>
              </Paper>
            </Grid>
          )}
        </Grid>
      )}

      {/* Registration / Edit Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {selectedDriver ? 'Edit Driver Profile' : 'Register New Driver'}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Drivers will use their Phone Number and a 4-digit PIN to log into the delivery application.
          </Typography>
          
          <Stack spacing={3} mt={2}>
            <TextField
              label="Full Name"
              fullWidth
              value={formData.full_name}
              onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
              required
            />
            
            <TextField
              label="Phone Number"
              fullWidth
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder="e.g. 918939..."
              required
            />
            
            <TextField
              label="Vehicle Number"
              fullWidth
              value={formData.vehicle_number}
              onChange={(e) => setFormData({ ...formData, vehicle_number: e.target.value })}
              placeholder="e.g. TN-01-AB-1234"
            />
            
            <Box>
              <TextField
                label={selectedDriver ? "Reset Login PIN (Leave blank to keep current)" : "Login PIN (4 Digits)"}
                type={showPin ? 'text' : 'password'}
                fullWidth
                value={formData.pin}
                onChange={(e) => setFormData({ ...formData, pin: e.target.value.replace(/[^0-9]/g, '').slice(0, 4) })}
                required={!selectedDriver}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPin(!showPin)} edge="end">
                        {showPin ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              {selectedDriver && (
                <Typography variant="caption" color="error" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                  <WarningIcon sx={{ fontSize: 12 }} /> Only reset if the driver has forgotten their PIN.
                </Typography>
              )}
            </Box>

            {selectedDriver && (
              <FormControlLabel
                control={
                  <Switch 
                    checked={formData.is_active} 
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })} 
                  />
                }
                label="Account Active"
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleCreateOrUpdate}
            disabled={submitting}
            startIcon={submitting && <CircularProgress size={20} />}
          >
            {selectedDriver ? 'Update Profile' : 'Create Profile'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DeleteIcon color="error" />
          Confirm Deletion
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete <strong>{selectedDriver?.full_name}</strong>? 
            This will permanently remove their access to the driver app.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button 
            color="error" 
            variant="contained" 
            onClick={confirmDelete} 
            disabled={submitting}
          >
            {submitting ? <CircularProgress size={20} /> : 'Delete Permanently'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
